import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import {
  Box,
  Button,
  Divider,
  Hidden,
  IconButton,
  Typography,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ArrowForward from '@mui/icons-material/ArrowForward';
import GetApp from '@mui/icons-material/GetApp';
import classNames from 'classnames';
import { FullScreenTrackingContext } from '../../../Rooms/Tracking/FullScreenTrackingContext';
import { getContentDownloadable } from '../../../../lib/Content';
import { RoomContentContextProps } from '../../../../lib/RoomContentContext';
import { RoomStructureContent } from '../../../../definitions/schemas';
import { SimplifiedTrackingProps } from '../../../Rooms/Tracking/types';
import { useI18n } from '../../../../lib/hook/useI18n';
import ContentType from '../../../../enums/ContentType';
import FullScreenDialog from '../../../Dialog/FullScreenDialog';
import TheaterMode from './TheaterMode';
import useAppMode from '../../../../lib/hook/config/useAppMode';
import useArrowKeys from '../../../../lib/hook/useArrowKeys';
import useDarkTheme from '../../../../theme/styles/useDarkTheme';
import { useCopilotMethods } from '../../../GlobalCopilot';
import { useCopilotAction } from '../../../GlobalCopilot/CopilotActionContext';
import { COPILOT_ACTION_TYPES } from '../../../GlobalCopilot/constants';
import { RoomViewContext } from '../../View/RoomViewContext';

const DIRECTION = {
  PREVIOUS: -2,
  NEXT: 0,
};

const DOWNLOADABLE_TYPES = [ContentType.DOCUMENT, ContentType.IMAGE];

interface IContext {
  displayContentOnTheaterMode: (contentId: string) => void;
}

export const TheaterModeContext: React.Context<IContext> =
  React.createContext<IContext>({
    displayContentOnTheaterMode: () => {},
  });

const useStyles = makeStyles((theme: Theme) => ({
  appBar: {
    borderBottom: '1px solid rgba(219, 219, 219, 0.2)',
    [theme.breakpoints.down('md')]: {
      '& $divider': {
        visibility: 'hidden',
      },
    },
  },
  button: {
    textTransform: 'none',
    minWidth: 'unset',
    padding: theme.spacing(0.75, 0),
    [theme.breakpoints.up('md')]: {
      minWidth: theme.spacing(8),
      padding: theme.spacing(0.75, 1),
    },
  },
  contentWrapper: {
    padding: theme.spacing(0, 2),
    width: '100%',
    [theme.breakpoints.up('md')]: {
      margin: 'auto',
      maxWidth: theme.spacing(92.5),
      padding: theme.spacing(0, 0, 1, 0),
    },
  },
  dialogPaper: {
    backgroundColor: 'initial',
  },
  divider: {
    [theme.breakpoints.up('md')]: {
      margin: theme.spacing(0, 3),
    },
  },
  layoutContent: {
    paddingBottom: theme.spacing(10),
    [theme.breakpoints.up('md')]: {
      paddingBottom: theme.spacing(0),
    },
  },
  stickyActions: {
    backgroundColor: 'var(--background-color-black-pastel-translucent)',
    bottom: 0,
    height: theme.spacing(5),
    position: 'fixed',
    width: '100%',
    '&::before': {
      backgroundColor: 'var(--divider-color-light-dark)',
      content: '""',
      display: 'block',
      height: '1px',
      opacity: 0.2,
      width: '100%',
    },
  },
}));

type Props = React.PropsWithChildren<{
  contextProps: RoomContentContextProps;
  referrer: string;
  structureContents: RoomStructureContent[];
  /**
   * Pool of openable contents used as a fallback when a requested content isn't
   * part of the root structure — namely contents nested in a folder (the
   * currently-open folder for tile clicks, or any folder for a copilot
   * @-mention). Defaults to `structureContents` when not provided. Prev/next
   * navigation still uses `structureContents` only.
   */
  allRoomContents?: RoomStructureContent[];
  TrackingProps?: SimplifiedTrackingProps;
  isBuyerCopilotEnabledForRoom?: boolean;
}>;

const TheaterModeContextProvider: React.FC<Props> = (
  props: Props
): JSX.Element => {
  const { fullScreenContentId } = useContext(FullScreenTrackingContext);

  const classes = useStyles();
  const darkThemeClasses = useDarkTheme();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { isEmbeddedMode } = useAppMode();
  const { t, tc } = useI18n();

  const {
    children,
    contextProps,
    referrer,
    structureContents,
    allRoomContents = structureContents,
    TrackingProps,
    isBuyerCopilotEnabledForRoom,
  } = props;

  const [open, setOpen] = useState(false);
  const [currentContent, setCurrentContent] = useState<RoomStructureContent>();
  const [playMediaImmediately, setPlayMediaImmediately] = useState(false);

  // convenience flag to trigger a content's download from some levels below
  // in the components tree, to avoid rising the state up to this context
  const [downloadTrigger, setDownloadTrigger] = useState(0);

  /**
   * We allow navigation with keyboard arrows between the room contents
   * only when the theater mode is opened and there's no other content in fullscreen
   * like an image or document.
   *
   * @note It's important to turn off the arrow navigation when it's not required,
   * as the useArrowKeys hook uses `preventDefault`, which affects the arrow
   * keys behavior in any input element in the page meanwhile this is active
   */
  const allowArrowsNavigation = open && !fullScreenContentId;

  useArrowKeys(allowArrowsNavigation, {
    onLeftKey: () => onSwitchContent(DIRECTION.PREVIOUS),
    onRightKey: () => onSwitchContent(DIRECTION.NEXT),
  });

  const contentIndex = useMemo(
    () =>
      structureContents.findIndex((item) => item.id === currentContent?.id) + 1,
    [currentContent, structureContents]
  );

  const contentWrapperMaxWidth = useMemo(() => {
    if (!currentContent) {
      return undefined;
    }

    const { match: content } = currentContent;

    switch (content.type) {
      case ContentType.URL:
        return content.url?.iframe ? '100%' : undefined;
      default:
        return undefined;
    }
  }, [currentContent]);

  const isDownloadDisabled = useMemo(() => {
    if (!currentContent) {
      return false;
    }

    const { match: content } = currentContent;

    // Disable the download button when vendor mode is enabled/within iframe
    // and the content to download is a document.
    return isEmbeddedMode && content.type === ContentType.DOCUMENT;
  }, [currentContent, isEmbeddedMode]);

  const contentDownloadable = useMemo(() => {
    if (!(currentContent && isMobile)) {
      return false;
    }

    const { match: content, isDownloadable = false } = currentContent;

    const allowsDownloads = DOWNLOADABLE_TYPES.includes(
      content.type as ContentType
    );

    return (
      allowsDownloads && (isDownloadable || getContentDownloadable(content))
    );
  }, [currentContent, isMobile]);

  const displayContentOnTheaterMode = useCallback(
    (contentId: string) => {
      // Prefer the root structure item (keeps prev/next navigation consistent),
      // then fall back to the complete pool for folder-nested contents that
      // aren't part of the root structure.
      const structureContent =
        structureContents.find((item) => item.id === contentId) ??
        allRoomContents.find((item) => item.id === contentId);

      if (!structureContent) {
        return;
      }

      setPlayMediaImmediately(true);
      setCurrentContent(structureContent);
      setOpen(true);
    },
    [structureContents, allRoomContents]
  );

  const copilotAction = useCopilotAction();

  // Single, authoritative handler for copilot OPEN_CONTENT actions. Previously
  // each mounted <RoomStructureContent> listened for this and opened itself,
  // which failed for folder-nested contents (their tiles aren't mounted on the
  // room page). Handling it here — where the full content pool is available —
  // lets any room content be opened regardless of folder placement.
  useEffect(() => {
    const action = copilotAction?.currentAction;
    if (action?.type !== COPILOT_ACTION_TYPES.OPEN_CONTENT) {
      return;
    }
    const contentId = action.payload.id;
    if (!contentId) {
      return;
    }
    displayContentOnTheaterMode(contentId);
    copilotAction?.clearAction();
  }, [copilotAction, displayContentOnTheaterMode]);

  const onClose = () => {
    setCurrentContent(undefined);
    setOpen(false);
    setDownloadTrigger(0);
  };

  const onSwitchContent = (offset: number) => {
    const index = contentIndex + offset;
    if (index < 0 || index >= structureContents.length) {
      return;
    }

    const structureContent = structureContents[index];
    setPlayMediaImmediately(false);
    setDownloadTrigger(0);
    setCurrentContent(structureContent);
  };

  const onDownload = () => {
    setDownloadTrigger(Date.now());
  };

  const renderDownloadIconButton = (): JSX.Element => (
    <IconButton
      aria-label="Download"
      className={classNames(darkThemeClasses.primaryTextLight, {
        [darkThemeClasses.disabledTextLight]: isDownloadDisabled,
      })}
      disabled={isDownloadDisabled}
      onClick={onDownload}
      size="small"
      title="Download"
    >
      <GetApp />
    </IconButton>
  );

  const renderDownloadButton = (): JSX.Element | undefined => {
    if (!contentDownloadable) {
      return undefined;
    }

    return (
      <>
        {isDownloadDisabled ? (
          <Tooltip
            arrow
            placement="top"
            PopperProps={{ disablePortal: true }}
            title="Downloads are only enabled in the room link"
            enterTouchDelay={0}
            leaveTouchDelay={5000}
          >
            <a>{renderDownloadIconButton()}</a>
          </Tooltip>
        ) : (
          renderDownloadIconButton()
        )}
        <Divider
          className={classNames(classes.divider, darkThemeClasses.divider)}
          flexItem
          orientation="vertical"
        />
      </>
    );
  };

  const renderNavigation = (): JSX.Element => {
    const isPreviousDisabled = contentIndex === 1;
    const isNextDisabled = contentIndex === structureContents.length;

    return (
      <Box display="flex">
        <Box flex="1">
          <Button
            className={classNames(
              classes.button,
              darkThemeClasses.primaryTextLight,
              { [darkThemeClasses.disabledTextLight]: isPreviousDisabled }
            )}
            color="inherit"
            disabled={contentIndex === 1}
            fullWidth
            onClick={() => onSwitchContent(DIRECTION.PREVIOUS)}
            startIcon={<ArrowBack />}
          >
            {t('theater_mode.previous')}
          </Button>
        </Box>
        <Divider
          className={classNames(classes.divider, darkThemeClasses.divider)}
          flexItem
          orientation="vertical"
        />
        <Box flex="1">
          <Button
            className={classNames(
              classes.button,
              darkThemeClasses.primaryTextLight,
              { [darkThemeClasses.disabledTextLight]: isNextDisabled }
            )}
            color="inherit"
            disabled={contentIndex === structureContents.length}
            endIcon={<ArrowForward />}
            fullWidth
            onClick={() => onSwitchContent(DIRECTION.NEXT)}
          >
            {t('theater_mode.next')}
          </Button>
        </Box>
      </Box>
    );
  };

  const appBarBackground = 'var(--background-color-black-pastel)';
  const layoutBackground = 'var(--background-color-black-pastel-translucent)';

  const context = {
    displayContentOnTheaterMode,
  };

  const copilotMethods = useCopilotMethods();
  const { isAnonymousContact, verifyContactIdentity } =
    useContext(RoomViewContext);

  const onBuyerCopilotClick = useCallback(() => {
    if (isAnonymousContact) {
      verifyContactIdentity('Deal Room - Buyer Copilot');
      return;
    }

    if (currentContent?.match) {
      copilotMethods?.sendContentToCopilot({
        contentName: currentContent.match.name ?? '',
        contentId: currentContent.match.id?.toString() || '',
      });
    }
  }, [
    currentContent,
    copilotMethods,
    isAnonymousContact,
    verifyContactIdentity,
  ]);

  const isEmbeddableContent =
    currentContent?.match?.type === ContentType.EMBEDDABLE;

  return (
    <TheaterModeContext.Provider value={context}>
      {children}
      <FullScreenDialog
        isBuyerCopilotEnabledForRoom={
          isBuyerCopilotEnabledForRoom && !isEmbeddableContent
        }
        onBuyerCopilotClick={onBuyerCopilotClick}
        classes={{
          appBar: classNames(classes.appBar, darkThemeClasses.primaryTextLight),
          divider: classNames(classes.divider, darkThemeClasses.divider),
          paper: classes.dialogPaper,
        }}
        closeButton={
          <Button
            className={classNames(
              classes.button,
              darkThemeClasses.primaryTextLight
            )}
            color="inherit"
            startIcon={<ArrowBack />}
          >
            <Hidden mdDown>
              <Typography color="inherit" variant="body1">
                {t('theater_mode.back')}
              </Typography>
            </Hidden>
          </Button>
        }
        LayoutAppBarProps={{ background: appBarBackground }}
        LayoutContentProps={{
          background: layoutBackground,
          className: classes.layoutContent,
        }}
        onClose={onClose}
        open={open}
        primaryAction={
          <>
            {renderDownloadButton()}
            <Hidden mdDown>{renderNavigation()}</Hidden>
          </>
        }
        title={
          <Box
            flex="1"
            mr={{ xs: contentDownloadable ? 3.75 : 8, md: 0 }}
            textAlign={{ xs: 'center', md: 'left' }}
          >
            <Typography color="inherit">
              {tc(
                'theater_mode.content',
                {
                  index: contentIndex,
                  contents: structureContents.length,
                },
                {
                  0: (
                    <Box
                      className={darkThemeClasses.secondaryTextLight}
                      component="span"
                    />
                  ),
                }
              )}
            </Typography>
          </Box>
        }
      >
        <Box
          className={classes.contentWrapper}
          style={{ maxWidth: contentWrapperMaxWidth }}
        >
          {currentContent && (
            <TheaterMode
              content={currentContent}
              contentDownloadTrigger={downloadTrigger}
              contextProps={contextProps}
              disableDocumentsDownloadButton={isMobile}
              key={currentContent.id}
              playMediaImmediately={playMediaImmediately}
              referrer={referrer}
              TrackingProps={TrackingProps}
            />
          )}
        </Box>

        <Hidden mdUp>
          <Box className={classes.stickyActions}>{renderNavigation()}</Box>
        </Hidden>
      </FullScreenDialog>
    </TheaterModeContext.Provider>
  );
};

export default TheaterModeContextProvider;
